import { useMemo, useState, type DragEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, Select, Tabs } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { daysSince, money, monthLabel, relativeTime } from '@/lib/format';
import { getPipelines, listDeals } from '@/data/client';
import { useMembers } from '@/data/hooks';
import type { Deal, DealStatus, Pipeline as PipelineT, SourceBucket } from '@/data/types';
import { useSearch } from '@/components/shell/search';
import { BUCKET_LABEL, BUCKET_OPTIONS } from '@/components/crm/SourceBadge';
import { useDealMover } from '@/components/crm/DealMove';
import { DealFormModal } from '@/components/crm/DealForm';
import { FormError } from '@/components/crm/Modal';

const STALE_DAYS = 14;
const todayIso = () => new Date().toLocaleDateString('en-CA');
const shortDate = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
type View = 'board' | 'list';
type Stage = PipelineT['stages'][number];

/* Filters run client-side: the whole book fits comfortably in one response for
   years, and it keeps drag-and-drop instant. Source and campaign filter on the
   deal's FIRST-TOUCH lead — the reporting default — so "what is paid search
   producing" reads straight off the board. */
export function Pipeline() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { query } = useSearch();
  const { ownerOptions } = useMembers();
  const [view, setView] = useState<View>('board');
  const [listStatus, setListStatus] = useState<DealStatus | 'all'>('open');
  const [owner, setOwner] = useState('');
  const [bucket, setBucket] = useState<SourceBucket | 'none' | ''>('');
  const [campaign, setCampaign] = useState('');
  const [creating, setCreating] = useState(false);
  const { data: pipelines } = useQuery({ queryKey: ['pipelines'], queryFn: getPipelines, staleTime: 10 * 60_000 });
  const { data: allDeals = [] } = useQuery({ queryKey: ['deals', 'all'], queryFn: () => listDeals() });
  const pipeline = pipelines?.find((p) => p.isDefault) ?? pipelines?.[0];

  const mover = useDealMover();
  const move = (deal: Deal, stage: Stage) => {
    if (stage.kind === 'open' && stage.id !== deal.stageId) {
      // Optimistic: the card lands immediately; the refetch after the move confirms it.
      qc.setQueryData<Deal[]>(['deals', 'all'], (prev) => prev?.map((d) => (d.id === deal.id
        ? { ...d, stageId: stage.id, stageName: stage.name, stageKind: 'open', status: 'open', stageEnteredAt: new Date().toISOString() } : d)));
    }
    mover.request(deal, stage);
  };

  const campaigns = useMemo(
    () => [...new Set(allDeals.map((d) => d.source?.campaignKey).filter((k): k is string => !!k))].sort(),
    [allDeals],
  );
  const deals = allDeals.filter((d) => {
    if (owner && d.ownerUserId !== owner) return false;
    if (bucket === 'none' && d.source) return false;
    if (bucket && bucket !== 'none' && d.source?.bucket !== bucket) return false;
    if (campaign && d.source?.campaignKey !== campaign) return false;
    if (query) {
      const q = query.toLowerCase();
      if (!d.name.toLowerCase().includes(q) && !d.companyName.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="crm-list">
      <div className="lead-toolbar">
        <Tabs items={[{ value: 'board', label: 'Board' }, { value: 'list', label: 'List' }]} value={view} onChange={(v) => setView(v as View)} />
        <div className="lead-toolbar__filters">
          <label className="lead-field">
            <Icon name="users" size={16} />
            <Select aria-label="Owner" options={[{ value: '', label: 'All owners' }, ...ownerOptions]} value={owner} onChange={(e) => setOwner(e.target.value)} />
          </label>
          <label className="lead-field">
            <Icon name="filter" size={16} />
            <Select aria-label="Source" value={bucket} onChange={(e) => setBucket(e.target.value as SourceBucket | 'none' | '')}
              options={[{ value: '', label: 'All sources' }, ...BUCKET_OPTIONS, { value: 'none', label: 'Unattributed' }]} />
          </label>
          {campaigns.length > 0 && (
            <label className="lead-field">
              <Icon name="target" size={16} />
              <Select aria-label="Campaign" options={[{ value: '', label: 'All campaigns' }, ...campaigns.map((c) => ({ value: c, label: c }))]}
                value={campaign} onChange={(e) => setCampaign(e.target.value)} />
            </label>
          )}
        </div>
        <div className="lead-toolbar__right">
          <Button iconLeft={<Icon name="plus" size={16} />} onClick={() => setCreating(true)}>New deal</Button>
        </div>
      </div>
      <FormError error={mover.error} />

      {pipeline && view === 'board' && <Board pipeline={pipeline} deals={deals} onMove={move} onOpen={(d) => navigate(`/deals/${d.id}`)} />}
      {pipeline && view === 'list' && (
        <DealList deals={deals} status={listStatus} onStatus={setListStatus} onOpen={(d) => navigate(`/deals/${d.id}`)} />
      )}

      {mover.ui}
      {creating && <DealFormModal onClose={() => setCreating(false)} />}
    </div>
  );
}

function Board({ pipeline, deals, onMove, onOpen }: { pipeline: PipelineT; deals: Deal[]; onMove: (d: Deal, s: Stage) => void; onOpen: (d: Deal) => void }) {
  const [over, setOver] = useState<string | null>(null);
  const byId = new Map(deals.map((d) => [d.id, d]));
  const recent = (d: Deal) => d.closedAt != null && daysSince(d.closedAt) <= 30;
  const drop = (stage: Stage) => (e: DragEvent) => {
    e.preventDefault();
    setOver(null);
    const deal = byId.get(e.dataTransfer.getData('text/deal-id'));
    if (deal) onMove(deal, stage);
  };
  const target = (stage: Stage) => ({
    onDragOver: (e: DragEvent) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setOver(stage.id); },
    onDragLeave: () => setOver((o) => (o === stage.id ? null : o)),
    onDrop: drop(stage),
  });

  return (
    <div className="crm-board">
      {pipeline.stages.filter((s) => s.kind === 'open').map((stage) => {
        const col = deals.filter((d) => d.status === 'open' && d.stageId === stage.id);
        const mrr = col.reduce((n, d) => n + (d.mrr ?? 0), 0);
        return (
          <section key={stage.id} className={`crm-board__col ${stage.nurture ? 'crm-board__col--nurture' : ''} ${over === stage.id ? 'is-over' : ''}`} {...target(stage)}>
            <header className="crm-board__head">
              <h3>{stage.name}</h3>
              {stage.nurture
                ? <span title="Alive but not being worked — kept out of pipeline value and stale alerts">{col.length} · nurture</span>
                : <span>{col.length}{mrr ? ` · ${money(mrr)}/mo` : ''}</span>}
            </header>
            <div className="crm-board__cards">
              {col.map((d) => <DealCard key={d.id} deal={d} onOpen={onOpen} />)}
            </div>
          </section>
        );
      })}
      <div className="crm-board__ends">
        {pipeline.stages.filter((s) => s.kind !== 'open').map((stage) => {
          const n = deals.filter((d) => d.stageId === stage.id && recent(d)).length;
          return (
            <section key={stage.id} className={`crm-board__end crm-board__end--${stage.kind} ${over === stage.id ? 'is-over' : ''}`} {...target(stage)}>
              <Icon name={stage.kind === 'won' ? 'award' : 'x'} size={18} />
              <strong>{stage.name}</strong>
              <span>{n} in last 30 days</span>
              <small>Drop a deal here</small>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function DealCard({ deal, onOpen }: { deal: Deal; onOpen: (d: Deal) => void }) {
  const days = daysSince(deal.stageEnteredAt);
  return (
    <article
      className="crm-card"
      draggable
      onDragStart={(e) => { e.dataTransfer.setData('text/deal-id', deal.id); e.dataTransfer.effectAllowed = 'move'; }}
      onClick={() => onOpen(deal)}
    >
      <strong>{deal.name}</strong>
      {deal.name !== deal.companyName && <span className="crm-cell-sub">{deal.companyName}</span>}
      <div className="crm-card__row">
        <span className="crm-card__mrr">{deal.mrr != null ? `${money(deal.mrr)}/mo` : 'No value yet'}</span>
        {deal.followUpAt && <span className={`crm-card__follow ${deal.followUpAt <= todayIso() ? 'is-due' : ''}`} title={deal.followUpNote ?? 'Follow-up'}>
          <Icon name="clock" size={12} /> {shortDate(deal.followUpAt)}</span>}
        <span className={`crm-card__days ${days >= STALE_DAYS && !deal.stageNurture ? 'is-stale' : ''}`} title="Days in this stage">{days}d</span>
      </div>
      <div className="crm-card__row">
        <span className={`crm-src__bucket crm-src__bucket--${deal.source?.bucket ?? 'none'}`}>{deal.source ? BUCKET_LABEL[deal.source.bucket] : 'Unattributed'}</span>
        {deal.ownerName && <span className="crm-cell-sub">{deal.ownerName.split(' ')[0]}</span>}
      </div>
    </article>
  );
}

const STATUS_TABS = [{ value: 'open', label: 'Open' }, { value: 'won', label: 'Won' }, { value: 'lost', label: 'Lost' }, { value: 'all', label: 'All' }];

function DealList({ deals, status, onStatus, onOpen }: { deals: Deal[]; status: DealStatus | 'all'; onStatus: (s: DealStatus | 'all') => void; onOpen: (d: Deal) => void }) {
  const rows = deals.filter((d) => status === 'all' || d.status === status);
  const totalMrr = rows.reduce((n, d) => n + (d.mrr ?? 0), 0);
  return (
    <Card padding={false}>
      <div className="crm-section__head crm-section" style={{ marginBottom: 0 }}>
        <Tabs items={STATUS_TABS} value={status} onChange={(v) => onStatus(v as DealStatus | 'all')} />
        <span className="crm-cell-muted">{rows.length} deals · {money(totalMrr)}/mo</span>
      </div>
      <table className="dash-table lead-table">
        <thead>
          <tr><th>Deal</th><th>Stage</th><th className="crm-num">MRR</th><th>Owner</th><th>First touch</th><th>Lead month</th><th>{status === 'open' ? 'In stage' : 'Closed'}</th></tr>
        </thead>
        <tbody>
          {rows.map((d) => (
            <tr key={d.id} onClick={() => onOpen(d)}>
              <td><strong>{d.name}</strong><span className="crm-cell-sub"><Link to={`/companies/${d.companyId}`} onClick={(e) => e.stopPropagation()}>{d.companyName}</Link></span></td>
              <td>{d.stageName}{d.lostReason && <span className="crm-cell-sub">{d.lostReason}</span>}</td>
              <td className="crm-num">{d.mrr != null ? money(d.mrr) : '—'}</td>
              <td className={d.ownerName ? '' : 'crm-cell-muted'}>{d.ownerName ?? 'Unassigned'}</td>
              <td>
                {d.source
                  ? <><span className={`crm-src__bucket crm-src__bucket--${d.source.bucket}`}>{BUCKET_LABEL[d.source.bucket]}</span>
                      {(d.source.sourceLabel ?? d.source.utmCampaign) && <span className="crm-cell-sub">{d.source.sourceLabel ?? d.source.utmCampaign}</span>}</>
                  : <span className="crm-cell-muted">Unattributed</span>}
              </td>
              <td className="crm-cell-muted">{monthLabel(d.cohortDate)}</td>
              <td className="crm-cell-muted">{d.status === 'open' ? `${daysSince(d.stageEnteredAt)}d` : relativeTime(d.closedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!rows.length && <p className="lead-none">No deals here.</p>}
    </Card>
  );
}
